import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_enable-referral-hub/artifacts/rvh4rx39_Enable%20Healthcare%20emblem_wordmark_White.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_4822f831-c6f6-4bc2-838f-0fa5f2e6db02/artifacts/e77i9jga_iStock-2231357490.jpg";
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Microsoft icon component
const MicrosoftIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
    <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
  </svg>
);

const Login = () => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success('Account created successfully!');
      }
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fillDemoCredentials = (type) => {
    if (type === 'admin') {
      setFormData({ email: 'admin@enablehealthcare.com', password: 'admin123', name: '' });
    } else {
      setFormData({ email: 'agent@enablehealthcare.com', password: 'agent123', name: '' });
    }
  };

  const handleMicrosoftLogin = async () => {
    // Fetch the Microsoft auth URL from backend (avoids redirect caching)
    try {
      const res = await fetch(`${API_URL}/api/auth/microsoft/auth-url`);
      const data = await res.json();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        alert(data.detail || 'Microsoft OAuth not configured');
      }
    } catch (e) {
      alert('Failed to connect to Microsoft. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full mx-auto"
        >
          {/* Logo for mobile */}
          <div className="lg:hidden mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_enable-referral-hub/artifacts/1e30d90o_Enable%20Healthcare%20emblem_wordmark_BLUE.png" 
              alt="Enable Healthcare" 
              className="h-12"
            />
          </div>

          <div className="mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#1B365D] tracking-tight">
              {isLogin ? 'Welcome back' : 'Create account'}
            </h1>
            <p className="mt-3 text-slate-500 text-base">
              {isLogin 
                ? 'Sign in to access your referral portal' 
                : 'Join our partner network today'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-700">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="h-12 rounded-xl border-slate-200 focus:border-[#0071E3] focus:ring-[#0071E3]/20"
                  data-testid="register-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="you@example.com"
                className="h-12 rounded-xl border-slate-200 focus:border-[#0071E3] focus:ring-[#0071E3]/20"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="h-12 rounded-xl border-slate-200 focus:border-[#0071E3] focus:ring-[#0071E3]/20 pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isLogin && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    const email = prompt('Enter your email address for password reset:');
                    if (email) {
                      fetch(`${API_URL}/api/auth/password-reset/request`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email })
                      })
                      .then(res => res.json())
                      .then(data => {
                        toast.success('If an account exists, you will receive reset instructions.');
                        if (data.reset_token) {
                          // For testing only - show reset token
                          const newPass = prompt('Enter your new password:');
                          if (newPass) {
                            fetch(`${API_URL}/api/auth/password-reset/confirm`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ token: data.reset_token, new_password: newPass })
                            })
                            .then(res => res.json())
                            .then(() => toast.success('Password reset successfully!'))
                            .catch(() => toast.error('Password reset failed'));
                          }
                        }
                      })
                      .catch(() => toast.error('Request failed'));
                    }
                  }}
                  className="text-sm text-[#0071E3] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1B365D] hover:bg-[#1B365D]/90 text-white rounded-full font-medium text-base transition-all active:scale-[0.98]"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-slate-500">or continue with</span>
              </div>
            </div>

            {/* Microsoft Login Button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleMicrosoftLogin}
              className="w-full h-12 rounded-full font-medium text-base border-slate-200 hover:bg-slate-50 transition-all"
              data-testid="microsoft-login-btn"
            >
              <MicrosoftIcon />
              <span className="ml-3">Sign in with Microsoft</span>
            </Button>
          </form>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-[#0071E3] hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          {/* Demo Credentials */}
          {isLogin && (
            <div className="mt-8 p-4 bg-slate-50 rounded-2xl">
              <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wide">Demo Accounts</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemoCredentials('admin')}
                  className="flex-1 rounded-lg text-xs"
                  data-testid="demo-admin-btn"
                >
                  Admin
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemoCredentials('agent')}
                  className="flex-1 rounded-lg text-xs"
                  data-testid="demo-agent-btn"
                >
                  Agent
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#1B365D]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Healthcare professional"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1B365D]/80 to-[#0071E3]/40" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between h-full p-12">
          <img 
            src={LOGO_URL} 
            alt="Enable Healthcare" 
            className="h-12 w-auto object-contain self-start"
            data-testid="hero-logo"
          />
          
          <div className="max-w-lg">
            <h2 className="font-heading text-4xl font-bold text-white mb-4 leading-tight">
              Partner Portal for Healthcare Sales Excellence
            </h2>
            <p className="text-white/80 text-lg leading-relaxed">
              Manage referrals, track commissions, and access the tools you need to succeed in healthcare sales.
            </p>
          </div>

          <div className="flex items-center gap-6 text-white/60 text-sm">
            <span>Trusted by 500+ Partners</span>
            <span className="w-1 h-1 rounded-full bg-white/40" />
            <span>$10M+ in Referral Commissions</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
